import type { User } from "./auth";

const STORAGE_KEY = "prive_device_accounts_v1";
const REMEMBER_DAYS = 15;

export type DeviceAccount = User & {
  rememberedAt: number;
  expiresAt: number;
};

function availableStorage(kind: "local" | "session") {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function read(storage: Storage | null, now = Date.now()) {
  if (!storage) return [] as DeviceAccount[];
  try {
    const value = JSON.parse(storage.getItem(STORAGE_KEY) || "[]") as unknown;
    if (!Array.isArray(value)) return [];
    const accounts = value.filter(
      (item): item is DeviceAccount =>
        Boolean(
          item &&
            typeof item === "object" &&
            "id" in item &&
            "displayName" in item &&
            "role" in item &&
            "expiresAt" in item &&
            typeof item.id === "string" &&
            typeof item.displayName === "string" &&
            (item.role === "admin" || item.role === "member") &&
            typeof item.expiresAt === "number" &&
            item.expiresAt > now,
        ),
    );
    if (accounts.length !== value.length)
      storage.setItem(STORAGE_KEY, JSON.stringify(accounts));
    return accounts;
  } catch {
    storage.removeItem(STORAGE_KEY);
    return [];
  }
}

export function deviceAccounts(now = Date.now()) {
  const merged = new Map<string, DeviceAccount>();
  for (const account of [
    ...read(availableStorage("local"), now),
    ...read(availableStorage("session"), now),
  ]) {
    const previous = merged.get(account.id);
    if (!previous || account.rememberedAt > previous.rememberedAt)
      merged.set(account.id, account);
  }
  return [...merged.values()].sort((a, b) => b.rememberedAt - a.rememberedAt);
}

export function rememberDeviceAccount(
  user: User,
  options: { persistent?: boolean; now?: number } = {},
) {
  const persistent = options.persistent ?? true;
  const now = options.now ?? Date.now();
  const target = availableStorage(persistent ? "local" : "session");
  const other = availableStorage(persistent ? "session" : "local");
  const expiresAt = persistent
    ? now + REMEMBER_DAYS * 86_400_000
    : now + 12 * 3_600_000;
  const next: DeviceAccount = { ...user, rememberedAt: now, expiresAt };
  if (target) {
    const accounts = read(target, now).filter((item) => item.id !== user.id);
    target.setItem(STORAGE_KEY, JSON.stringify([next, ...accounts]));
  }
  if (other) {
    const accounts = read(other, now).filter((item) => item.id !== user.id);
    if (accounts.length) other.setItem(STORAGE_KEY, JSON.stringify(accounts));
    else other.removeItem(STORAGE_KEY);
  }
  return next;
}

export function forgetDeviceAccount(userId: string) {
  for (const storage of [availableStorage("local"), availableStorage("session")]) {
    if (!storage) continue;
    const accounts = read(storage).filter((item) => item.id !== userId);
    if (accounts.length) storage.setItem(STORAGE_KEY, JSON.stringify(accounts));
    else storage.removeItem(STORAGE_KEY);
  }
}

